import Ajv2020, { type ErrorObject } from "ajv/dist/2020.js";
import storySchema from "../../schemas/story.schema.json";
import uiSchema from "../../schemas/ui.schema.json";
import type { Answer, Story, UiContent } from "./types";

const ajv = new Ajv2020({ allErrors: true, strict: true });
const validateStoryShape = ajv.compile<Story>(storySchema);
const validateUiShape = ajv.compile<UiContent>(uiSchema);

function formatErrors(errors: ErrorObject[] | null | undefined): string[] {
  return (errors ?? []).map(
    ({ instancePath, message }) => `${instancePath || "/"} ${message ?? "is invalid"}`,
  );
}

const routeKey = (choices: Answer[]) => choices.join("");

function routeHistory(
  route: Story["routes"][number],
  beforeDecision: number,
): { sceneId: string; answer: Answer }[] {
  return route.sceneIds.slice(0, beforeDecision - 1).map((sceneId, index) => ({
    sceneId,
    answer: route.choices[index],
  }));
}

function conditionMatches(
  history: { sceneId: string; answer: Answer }[],
  conditions: { sceneId: string; answer: Answer }[],
): boolean {
  return conditions.every((condition) =>
    history.some(
      (entry) => entry.sceneId === condition.sceneId && entry.answer === condition.answer,
    ),
  );
}

export function validateStory(value: unknown): Story {
  if (!validateStoryShape(value)) {
    throw new Error(formatErrors(validateStoryShape.errors).join("\n"));
  }

  const errors: string[] = [];
  const keys = new Set<string>();
  const usedEndingCategories = new Set<string>();
  const sceneKeys = new Set(Object.keys(value.scenes));
  const endingKeys = new Set(Object.keys(value.endings));
  const momentIds = new Set<string>();

  for (const [key, scene] of Object.entries(value.scenes)) {
    if (scene.id !== key) errors.push(`Scene key ${key} differs from its id ${scene.id}`);
    for (const answer of ["yes", "no"] as const) {
      const momentId = scene.choices[answer].moment.id;
      if (momentIds.has(momentId)) errors.push(`Duplicate moment id ${momentId}`);
      momentIds.add(momentId);
    }
    (scene.variants ?? []).forEach((variant, variantIndex) => {
      const conditionScenes = new Set<string>();
      variant.when.historyIncludes.forEach((condition) => {
        const conditionScene = value.scenes[condition.sceneId];
        if (conditionScenes.has(condition.sceneId)) {
          errors.push(`Scene ${key} variant ${variantIndex} repeats condition scene ${condition.sceneId}`);
        }
        conditionScenes.add(condition.sceneId);
        if (!conditionScene) {
          errors.push(`Scene ${key} variant ${variantIndex} references missing scene ${condition.sceneId}`);
        } else if (conditionScene.decision >= scene.decision) {
          errors.push(`Scene ${key} variant ${variantIndex} looks at non-earlier scene ${condition.sceneId}`);
        }
      });
    });
  }
  for (const [key, ending] of Object.entries(value.endings)) {
    if (ending.id !== key) errors.push(`Ending key ${key} differs from its id ${ending.id}`);
  }

  value.routes.forEach((route) => {
    const key = routeKey(route.choices);
    if (keys.has(key)) errors.push(`Duplicate route ${key}`);
    keys.add(key);

    route.sceneIds.forEach((sceneId, decisionIndex) => {
      const scene = value.scenes[sceneId];
      if (!sceneKeys.has(sceneId)) errors.push(`Route ${key} references missing scene ${sceneId}`);
      if (scene && scene.decision !== decisionIndex + 1) {
        errors.push(`Scene ${sceneId} is used at decision ${decisionIndex + 1}`);
      }
    });

    const ending = value.endings[route.endingId];
    if (!endingKeys.has(route.endingId)) errors.push(`Route ${key} references missing ending ${route.endingId}`);
    if (ending) usedEndingCategories.add(ending.category);

    const playedMomentIds = new Set(
      route.sceneIds.map((sceneId, index) =>
        value.scenes[sceneId]?.choices[route.choices[index]]?.moment.id,
      ),
    );
    route.recapMomentIds.forEach((momentId) => {
      if (!playedMomentIds.has(momentId)) {
        errors.push(`Route ${key} recap ${momentId} was not played on that route`);
      }
    });

    if (route.endingBody) {
      const causalMoments = [
        value.scenes[route.sceneIds[2]]?.choices[route.choices[2]]?.moment.id,
        value.scenes[route.sceneIds[5]]?.choices[route.choices[5]]?.moment.id,
      ];
      route.recapMomentIds.forEach((momentId, index) => {
        if (momentId !== causalMoments[index]) {
          errors.push(`Route ${key} causal recap ${index + 1} must be ${causalMoments[index]}`);
        }
      });
    }
  });

  const routeEndingBodies = value.routes.filter((route) => route.endingBody).length;
  if (routeEndingBodies !== 0 && routeEndingBodies !== value.routes.length) {
    errors.push("Route-specific endingBody must be provided for every route or no routes");
  }

  for (const scene of Object.values(value.scenes)) {
    const variants = scene.variants ?? [];
    if (!variants.length) continue;
    const matchingRoutes = value.routes.filter(
      (route) => route.sceneIds[scene.decision - 1] === scene.id,
    );
    const usedVariants = new Set<number>();
    matchingRoutes.forEach((route) => {
      const history = routeHistory(route, scene.decision);
      const matches = variants.flatMap((variant, index) =>
        conditionMatches(history, variant.when.historyIncludes) ? [index] : [],
      );
      matches.forEach((index) => usedVariants.add(index));
      if (matches.length > 1) {
        errors.push(`Route ${routeKey(route.choices)} matches multiple variants of scene ${scene.id}`);
      }
    });
    variants.forEach((_, index) => {
      if (!usedVariants.has(index)) errors.push(`Scene ${scene.id} variant ${index} matches no route prefix`);
    });
  }

  for (let mask = 0; mask < 64; mask += 1) {
    const choices = Array.from({ length: 6 }, (_, bit) =>
      mask & (1 << bit) ? "yes" : "no",
    ) as Answer[];
    if (!keys.has(routeKey(choices))) errors.push(`Missing route ${routeKey(choices)}`);
  }

  for (let prefixLength = 0; prefixLength < 6; prefixLength += 1) {
    const groups = new Map<string, Set<string>>();
    value.routes.forEach((route) => {
      const prefix = routeKey(route.choices.slice(0, prefixLength));
      const ids = groups.get(prefix) ?? new Set<string>();
      ids.add(route.sceneIds[prefixLength]);
      groups.set(prefix, ids);
    });
    groups.forEach((ids, prefix) => {
      if (ids.size !== 1) errors.push(`Choice prefix ${prefix || "<start>"} maps to multiple next scenes`);
    });
  }

  if (value.routes[0]?.sceneIds[0] !== value.startSceneId) {
    errors.push("startSceneId differs from the first route scene");
  }
  for (const category of ["resisted", "survived_with_severe_consequences", "disintegrating"]) {
    if (!usedEndingCategories.has(category)) errors.push(`No route reaches ending category ${category}`);
  }
  if (errors.length) throw new Error(errors.join("\n"));
  return value;
}

export function validateUi(value: unknown): UiContent {
  if (!validateUiShape(value)) {
    throw new Error(formatErrors(validateUiShape.errors).join("\n"));
  }
  return value;
}
